import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import AiProgressRealtimeService from 'services/AiProgressRealtimeService'

const AiProgressContext = createContext()

export const useAiProgress = () => {
    const context = useContext(AiProgressContext)
    if (!context) {
        throw new Error('useAiProgress must be used within AiProgressProvider')
    }
    return context
}

export const AiProgressProvider = ({ children }) => {
    // Store progress state by caseId-mediatorId key
    const [progressState, setProgressState] = useState({})
    const unsubscribeRefs = useRef({})

    /**
     * Subscribe to progress updates for a specific case
     * @param {number} caseId - Case ID
     * @param {number} mediatorId - Mediator ID
     */
    const subscribeToCaseProgress = useCallback((caseId, mediatorId) => {
        if (!caseId || !mediatorId) {
            console.warn('subscribeToCaseProgress: caseId and mediatorId are required')
            return
        }

        const key = `${caseId}-${mediatorId}`

        // If already subscribed, return
        if (unsubscribeRefs.current[key]) {
            return
        }

        // Initialize progress state
        setProgressState(prev => ({
            ...prev,
            [key]: {
                stage: 0,
                stageProgress: 0,
                overallProgress: 0,
                status: 'pending',
                message: '',
                caseId,
                mediatorId,
            }
        }))

        // Subscribe to real-time updates
        console.log(`[AiProgressContext] 📡 Subscribing to progress updates for case ${caseId}, mediator ${mediatorId}`)
        
        const unsubscribe = AiProgressRealtimeService.subscribeToProgress(
            caseId,
            mediatorId,
            (progress) => {
                console.log(`[AiProgressContext] 📥 Progress update received:`, {
                    caseId,
                    mediatorId,
                    stage: progress.stage,
                    stageProgress: progress.stage_progress ?? progress.stageProgress,
                    overallProgress: progress.overall_progress ?? progress.overallProgress,
                    status: progress.status,
                    message: progress.message,
                })
                
                setProgressState(prev => ({
                    ...prev,
                    [key]: {
                        ...prev[key],
                        stage: progress.stage ?? 0,
                        stageProgress: progress.stage_progress ?? progress.stageProgress ?? 0,
                        overallProgress: progress.overall_progress ?? progress.overallProgress ?? 0,
                        status: progress.status || 'pending',
                        message: progress.message || '',
                    }
                }))
            }
        )
        
        unsubscribeRefs.current[key] = unsubscribe
    }, [])

    /**
     * Unsubscribe from progress updates for a specific case
     * @param {number} caseId - Case ID
     * @param {number} mediatorId - Mediator ID
     */
    const unsubscribeFromCaseProgress = useCallback((caseId, mediatorId) => {
        if (!caseId || !mediatorId) return

        const key = `${caseId}-${mediatorId}`
        const unsubscribe = unsubscribeRefs.current[key]

        if (unsubscribe) {
            unsubscribe()
            delete unsubscribeRefs.current[key]
            
            // Clear progress state
            setProgressState(prev => {
                const newState = { ...prev }
                delete newState[key]
                return newState
            })
        }
    }, [])

    /**
     * Get progress state for a specific case
     * @param {number} caseId - Case ID
     * @param {number} mediatorId - Mediator ID
     * @returns {Object|null} Progress state or null
     */
    const getCaseProgress = useCallback((caseId, mediatorId) => {
        if (!caseId || !mediatorId) return null
        const key = `${caseId}-${mediatorId}`
        return progressState[key] || null
    }, [progressState])

    /**
     * Reset progress for a specific case
     * @param {number} caseId - Case ID
     * @param {number} mediatorId - Mediator ID
     */
    const resetCaseProgress = useCallback((caseId, mediatorId) => {
        if (!caseId || !mediatorId) return

        const key = `${caseId}-${mediatorId}`
        setProgressState(prev => ({
            ...prev,
            [key]: {
                stage: 0,
                stageProgress: 0,
                overallProgress: 0,
                status: 'pending',
                message: '',
                caseId,
                mediatorId,
            }
        }))
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Unsubscribe from all channels
            Object.values(unsubscribeRefs.current).forEach(unsubscribe => {
                if (typeof unsubscribe === 'function') {
                    unsubscribe()
                }
            })
            unsubscribeRefs.current = {}
            AiProgressRealtimeService.unsubscribeAll()
        }
    }, [])

    const value = {
        progressState,
        subscribeToCaseProgress,
        unsubscribeFromCaseProgress,
        getCaseProgress,
        resetCaseProgress,
    }

    return (
        <AiProgressContext.Provider value={value}>
            {children}
        </AiProgressContext.Provider>
    )
}

export default AiProgressContext
